import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import dayjs, { Dayjs } from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { InventoryInboundImportLogsFilters } from "../../../../../models/inventory/InventoryAdmin";
import { InventoryInboundImportLogsStore } from "../../../stores/InventoryInboundImportLogsStore";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import InventoryCountImportLogsTable from "../../components/Inventory/InventoryCountImportLogsTable";

const DEFAULT_LIMIT = 20;

const parsePositiveInt = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
  return parsed;
};

const InventoryInboundImportLogs: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { userStore } = useStores();
  const { showError } = useNotification();
  const store = useMemo(() => new InventoryInboundImportLogsStore(), []);

  const searchParams = new URLSearchParams(location.search);

  const parseUrlParams = (): InventoryInboundImportLogsFilters => {
    const limit = parsePositiveInt(searchParams.get("limit")) || DEFAULT_LIMIT;
    return {
      page_no: parsePositiveInt(searchParams.get("page_no")) || 1,
      limit: Math.min(limit, 100),
      search: searchParams.get("search") || undefined,
      inventoryId: parsePositiveInt(searchParams.get("inventoryId")),
      customerId: parsePositiveInt(searchParams.get("customerId")),
      adminId: parsePositiveInt(searchParams.get("adminId")),
      start_date: searchParams.get("start_date") || undefined,
      end_date: searchParams.get("end_date") || undefined,
    };
  };

  const initialFilters = parseUrlParams();
  const [filters, setFilters] =
    useState<InventoryInboundImportLogsFilters>(initialFilters);
  const [searchTerm, setSearchTerm] = useState(initialFilters.search || "");
  const [draftStartDate, setDraftStartDate] = useState<Dayjs | null>(
    initialFilters.start_date ? dayjs(initialFilters.start_date) : null,
  );
  const [draftEndDate, setDraftEndDate] = useState<Dayjs | null>(
    initialFilters.end_date ? dayjs(initialFilters.end_date) : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const debouncedSearch = useDebounce(searchTerm, 300);

  const canView = userStore.hasPermission([EmployeeRoles.VIEW_INVENTORY]);

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (error && typeof error === "object") {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      return err.response?.data?.message || err.message || fallback;
    }
    return fallback;
  };

  useEffect(() => {
    if (!canView) return;

    setIsLoading(true);
    store
      .load(filters)
      .catch((error: unknown) => {
        showError(
          "Error",
          getApiErrorMessage(
            error,
            "Failed to load inbound import logs. Please try again later.",
          ),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, canView]);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    const nextParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        nextParams.set(key, String(value));
      }
    });
    navigate({ search: nextParams.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  useEffect(() => {
    const nextSearch = debouncedSearch.trim();
    setFilters((prev) => {
      const currentSearch = prev.search || "";
      if (currentSearch === nextSearch) return prev;
      return {
        ...prev,
        search: nextSearch || undefined,
        page_no: 1,
      };
    });
  }, [debouncedSearch]);

  const handleSearchSubmit = () => {
    const trimmedSearch = searchTerm.trim();
    setSearchTerm(trimmedSearch);
    setFilters((prev) => ({
      ...prev,
      search: trimmedSearch || undefined,
      page_no: 1,
    }));
  };

  const handleApply = () => {
    try {
      const startDate = draftStartDate?.isValid()
        ? draftStartDate.format("YYYY-MM-DD")
        : undefined;
      const endDate = draftEndDate?.isValid()
        ? draftEndDate.format("YYYY-MM-DD")
        : undefined;

      if (startDate && endDate && startDate > endDate) {
        showError("Error", "Start date must be before or equal to end date");
        return;
      }

      const trimmedSearch = searchTerm.trim();
      setSearchTerm(trimmedSearch);
      setFilters((prev) => ({
        ...prev,
        search: trimmedSearch || undefined,
        start_date: startDate,
        end_date: endDate,
        page_no: 1,
      }));
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to apply filters"),
      );
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setDraftStartDate(null);
    setDraftEndDate(null);
    setFilters({
      page_no: 1,
      limit: DEFAULT_LIMIT,
      inventoryId: initialFilters.inventoryId,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: Math.min(newLimit, 100),
      page_no: 1,
    }));
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!canView && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">
                Audit Inbound CSV Updates
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View inventory inbound import logs
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}

      {canView && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">
                Audit Inbound CSV Updates
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                {filters.inventoryId
                  ? `Inbound import logs for inventory ${filters.inventoryId}`
                  : "View inventory inbound import logs"}
              </CustomTypography>
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Grid2 container spacing={2} columns={12} padding={2}>
                <Grid2
                  size={{ xs: 12, sm: 9, md: 11, lg: 11 }}
                  sx={{ padding: 0 }}
                >
                  <CustomTextField
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit();
                      }
                    }}
                    width="100%"
                    placeholder="Search by customer name or inventory ID"
                    customStyles={{
                      backgroundColor:
                        theme.palette.customColors?.grayBackGroundColor,
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ width: "100%", height: 40 }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <DateRangePickerNew
                    placeholder="Created date range"
                    startDate={draftStartDate}
                    endDate={draftEndDate}
                    onRangeSelect={(range) => {
                      setDraftStartDate(range.startDate);
                      setDraftEndDate(range.endDate);
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={resetFilters}
                      customStyles={{ width: "100%" }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      customStyles={{ width: "100%" }}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {store.logs.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No inbound import logs match the current search criteria.
                  </CustomTypography>
                </Box>
              )}
              {store.logs.length > 0 && (
                <InventoryCountImportLogsTable
                  maxHeight={window.innerHeight - 350}
                  logs={store.logs}
                  limit={filters.limit || DEFAULT_LIMIT}
                  page_no={filters.page_no || 1}
                  total_count={store.totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(InventoryInboundImportLogs);
